export type TranscriptionSegment = {
    id?: number | string;
    callDetailId?: number;
    segmentId?: number;
    speakerId?: string | number | null;
    start?: number;
    end?: number;
    text?: string | null;
};

export type TranscriptionByCallIdResponse = {
    statusCode?: number;
    title?: string | null;
    message?: string | null;
    data?:
        | TranscriptionSegment[]
        | {
            segments?: TranscriptionSegment[];
            speakerOrder?: string[];
            totalDurationSeconds?: number;
        }
        | null;
};

export type EvaluationCitationDisplay = {
    id?: number;
    text: string;
    start?: number;
    speaker?: string;
    comment?: string;
};

export const TRANSCRIPTION_HUB_STATUS = {
    PROCESSING: 102,
    SUCCESS: 103,
    UNSUCCESS: 104,
} as const;

export type TranscriptionHubStatusPayload = {
    sessionId: string | null;
    callDetailId: number | null;
    status: number | null;
    tick: number;
};

const toStatusCode = (value: unknown): number | null => {
    const statusCode = Number(value);
    return Number.isFinite(statusCode) ? statusCode : null;
};

const unwrapTranscriptionBody = (payload: unknown): {
    statusCode: number | null;
    message: string | null;
    data: unknown;
} => {
    if (typeof payload === 'string') {
        try {
            return unwrapTranscriptionBody(JSON.parse(payload));
        } catch {
            return {statusCode: null, message: null, data: null};
        }
    }

    if (Array.isArray(payload)) {
        return {statusCode: 200, message: null, data: payload};
    }

    if (!payload || typeof payload !== 'object') {
        return {statusCode: null, message: null, data: null};
    }

    const record = payload as Record<string, unknown>;
    const statusCode = toStatusCode(record.statusCode ?? record.StatusCode);
    const messageValue = record.message ?? record.Message;
    const message = typeof messageValue === 'string' ? messageValue : null;
    const data = Object.prototype.hasOwnProperty.call(record, 'data')
        ? record.data
        : Object.prototype.hasOwnProperty.call(record, 'Data')
            ? record.Data
            : null;

    return {statusCode, message, data};
};

export const normalizeTranscriptionResponseSegments = (data: unknown): TranscriptionSegment[] => {
    if (typeof data === 'string') {
        try {
            return normalizeTranscriptionResponseSegments(JSON.parse(data));
        } catch {
            return [];
        }
    }

    if (Array.isArray(data)) {
        return data;
    }

    if (!data || typeof data !== 'object') {
        return [];
    }

    const record = data as {
        segments?: TranscriptionSegment[];
        Segments?: TranscriptionSegment[];
        data?: unknown;
        Data?: unknown;
    };

    if (Array.isArray(record.segments)) {
        return record.segments;
    }

    if (Array.isArray(record.Segments)) {
        return record.Segments;
    }

    const nested = record.data ?? record.Data;
    if (nested != null && nested !== data) {
        return normalizeTranscriptionResponseSegments(nested);
    }

    return [];
};

export type TranscriptionLoadResult =
    | {type: 'loaded'; segments: TranscriptionSegment[]}
    | {type: 'pending'}
    | {type: 'error'; message: string | null};

export const resolveTranscriptionByCallIdResult = (payload: unknown): TranscriptionLoadResult => {
    const {statusCode, message, data} = unwrapTranscriptionBody(payload);
    const segments = normalizeTranscriptionResponseSegments(data);

    if (segments.length > 0 || (statusCode === 200 && data != null)) {
        return {type: 'loaded', segments};
    }

    if (statusCode === 200 && data == null) {
        return {type: 'pending'};
    }

    return {type: 'error', message};
};

export const isTranscriptionPendingResponse = (response: TranscriptionByCallIdResponse) => {
    return resolveTranscriptionByCallIdResult(response).type === 'pending';
};

export const formatTranscriptionTimestamp = (seconds: number) => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    return `(${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')})`;
};

export const mapTranscriptionSpeakerLabel = (speakerId: string | number | null | undefined) => {
    if (speakerId === 0 || speakerId === '0') {
        return 'Оператор';
    }

    if (speakerId === 1 || speakerId === '1') {
        return 'Клієнт';
    }

    if (speakerId === 2 || speakerId === '2') {
        return 'Система';
    }

    const normalizedSpeakerId = String(speakerId ?? '').toLowerCase();

    if (normalizedSpeakerId === 'speaker_0') {
        return 'Оператор';
    }

    if (normalizedSpeakerId === 'speaker_1') {
        return 'Клієнт';
    }

    if (normalizedSpeakerId === 'speaker_2') {
        return 'Система';
    }

    return speakerId != null && speakerId !== '' ? String(speakerId) : 'Учасник';
};

export const formatTranscriptionSegments = (segments: TranscriptionSegment[] | null | undefined): string => {
    if (!Array.isArray(segments) || segments.length === 0) {
        return '- - -';
    }

    return segments
        .map((segment) => {
            const timestamp = formatTranscriptionTimestamp(segment.start ?? 0);
            const speaker = mapTranscriptionSpeakerLabel(segment.speakerId);
            const text = String(segment.text ?? '').trim();

            return `${timestamp} ${speaker}: ${text}`;
        })
        .join('\n\n');
};

export const parseTranscriptionTextToSegments = (text: string): TranscriptionSegment[] => {
    if (!text || text === '- - -') {
        return [];
    }

    const segments: TranscriptionSegment[] = [];

    text
        .split(/\n\n+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => {
            const match = part.match(/^\((\d{1,2}):(\d{2})\)\s*([^:]+):\s*([\s\S]*)$/);
            if (!match) {
                return;
            }

            segments.push({
                start: Number(match[1]) * 60 + Number(match[2]),
                speakerId: match[3].trim(),
                text: match[4].trim(),
            });
        });

    segments.forEach((segment, index) => {
        const nextStart = segments[index + 1]?.start;
        if (nextStart != null) {
            segment.end = nextStart;
        }
    });

    return segments;
};

export const getTranscriptionSegmentTimeRange = (
    segments: TranscriptionSegment[],
    index: number,
): { start: number; end: number } => {
    const segment = segments[index];
    const start = segment.start ?? 0;
    let end = segment.end;

    if (end == null || end <= start) {
        const nextStart = segments[index + 1]?.start;
        end = nextStart != null ? nextStart : start + 30;
    }

    return {start, end};
};

export const findActiveTranscriptionSegmentIndex = (
    segments: TranscriptionSegment[],
    currentTimeSec: number,
): number => {
    for (let index = 0; index < segments.length; index += 1) {
        const {start, end} = getTranscriptionSegmentTimeRange(segments, index);
        if (currentTimeSec >= start && currentTimeSec < end) {
            return index;
        }
    }

    return -1;
};

export const findTranscriptionSegmentIndexById = (
    segments: TranscriptionSegment[],
    segmentId: number | string | null | undefined,
): number => {
    if (segmentId == null || segmentId === '') {
        return -1;
    }

    const normalizedId = Number(segmentId);
    if (!Number.isFinite(normalizedId)) {
        return -1;
    }

    return segments.findIndex((segment) => Number(segment.id) === normalizedId);
};

export const findTranscriptionSegmentIndexByStart = (
    segments: TranscriptionSegment[],
    startSec: number,
): number => {
    if (!Number.isFinite(startSec)) {
        return -1;
    }

    const exactIndex = segments.findIndex((segment) => Math.abs((segment.start ?? 0) - startSec) < 0.01);
    if (exactIndex >= 0) {
        return exactIndex;
    }

    return findActiveTranscriptionSegmentIndex(segments, startSec);
};

export const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const segmentMatchesTranscriptionQuery = (
    segment: TranscriptionSegment,
    query: string,
): boolean => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return true;
    }

    const text = String(segment.text ?? '').toLowerCase();
    const speaker = mapTranscriptionSpeakerLabel(segment.speakerId).toLowerCase();

    return text.includes(normalizedQuery) || speaker.includes(normalizedQuery);
};

export const getTranscriptionMatchingSegmentIndices = (
    segments: TranscriptionSegment[],
    query: string,
): number[] => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
        return [];
    }

    return segments.reduce<number[]>((indices, segment, index) => {
        if (segmentMatchesTranscriptionQuery(segment, normalizedQuery)) {
            indices.push(index);
        }

        return indices;
    }, []);
};

export const countTranscriptionSearchMatches = (
    segments: TranscriptionSegment[],
    query: string,
): number => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
        return 0;
    }

    const regex = new RegExp(escapeRegExp(normalizedQuery), 'gi');

    return segments.reduce((total, segment) => {
        const matches = String(segment.text ?? '').match(regex);
        return total + (matches?.length ?? 0);
    }, 0);
};

export const resolveEvaluationCitationDisplay = (
    citation: {
        id?: number;
        text?: string;
        start?: number;
        speaker?: string;
        comment?: string;
    },
    segments: TranscriptionSegment[] = [],
): EvaluationCitationDisplay => {
    const comment = citation.comment ?? '';

    if (citation.id != null && segments.length > 0) {
        const segment = segments.find((item) => Number(item.id) === Number(citation.id));

        if (segment) {
            return {
                id: Number(citation.id),
                text: String(segment.text ?? '').trim(),
                start: segment.start != null ? Number(segment.start) : undefined,
                speaker: mapTranscriptionSpeakerLabel(segment.speakerId),
                comment,
            };
        }
    }

    return {
        id: citation.id,
        text: String(citation.text ?? '').trim(),
        start: citation.start,
        speaker: citation.speaker,
        comment,
    };
};

export const countTranscriptionTextSearchMatches = (text: string, query: string): number => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
        return 0;
    }

    const matches = text.match(new RegExp(escapeRegExp(normalizedQuery), 'gi'));
    return matches?.length ?? 0;
};
